// src/components/Auth/Login.js
import React, { useState, useEffect, useContext } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useNavigate, useLocation } from 'react-router-dom';
import useNotification from '../../hooks/useNotification';
import { GoogleLogin } from '@react-oauth/google';
import FroggySpinner from '../common/FroggySpinner';
import { getGoogleOAuthRedirectUri } from '../../utils/env';
import { AFTER_LOGIN_PATH } from '../../constants/routes';
import { landingFor } from '../../utils/juniorLanding';
import '../../styles/login.css';

// #34 security (2026-08-17): the ?redirect= param was piped straight
// into navigate() — a protocol-relative "//evil.com" or absolute URL is
// an open redirect (phishing sink post-login). Accept only same-origin
// internal paths: a single leading slash, not "//", no scheme.
function safeInternalPath(value) {
  if (typeof value !== 'string' || !value) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  // #34 review S2: browsers normalize backslash to '/', so '/\evil.com'
  // is protocol-relative — reject backslashes too.
  if (value.includes('\\')) return null;
  if (value.includes('://') || value.toLowerCase().includes('javascript:')) return null;
  return value;
}

const ERROR_MESSAGES = {
  google_account: "Please sign in using the Google button. This account was created using Google login.",
};

const Login = () => {
  const { login, loginWithGoogle, isAuthenticated, hydrated, loading, authReady, roles } = useContext(AuthContext);
  const { refreshCart, mergeCartItems, cartItems } = useCart();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const didRedirectRef = React.useRef(false);
  const submitDebounceRef = React.useRef(false);
  const lastInputTimestampRef = React.useRef(0);

  // Debug logging (can be removed after verification)
  const DEBUG_AUTH = false;
  if (DEBUG_AUTH) {
    console.log('[LOGIN] render:', { hydrated, loading, authReady, isAuthenticated });
  }

  useEffect(() => {
    // Only redirect once auth is fully settled and we haven't redirected yet
    if (!authReady || didRedirectRef.current) return;

    if (isAuthenticated) {
      didRedirectRef.current = true;
      if (DEBUG_AUTH) console.log('[LOGIN] redirecting authenticated user');

      // Compute target: prioritize location state, then query param, then default
      const from = location.state?.from?.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get('redirect');

      const target = from && from !== '/login' && from !== '/signup'
        ? from
        : safeInternalPath(redirect) || AFTER_LOGIN_PATH;

      // R-TRAINING (T9): a junior keeper's DEFAULT landing is Froggy U;
      // any other target, and anyone else, goes where they were going.
      landingFor(target, roles).then((next) => navigate(next, { replace: true }));
    }
  }, [authReady, isAuthenticated, navigate, location, roles]);

  const handlePostLogin = async () => {
    try {
      const { adjusted } = await mergeCartItems();
      if (adjusted) {
        showNotification("Some items in your cart were adjusted due to stock limits.", "warning");
      }
      await refreshCart();
      localStorage.setItem('postMergeCartHandled', 'true');
    } catch (err) {
      console.error("Cart merge error:", err);
      showNotification("Failed to merge your cart after login.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Debounce: drop rapid double-submits
    if (submitDebounceRef.current || isSubmitting) {
      return;
    }

    // Safari AutoFill guard: ignore submits within 180ms of last input
    const now = Date.now();
    const timeSinceLastInput = now - lastInputTimestampRef.current;
    if (timeSinceLastInput < 180) {
      // Too soon after AutoFill - ignore this submit
      return;
    }

    submitDebounceRef.current = true;
    setIsSubmitting(true);
    setError(null);

    try {
      await login(email, password, cartItems, handlePostLogin);
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;

      // dj-rest-auth returns errors in multiple shapes:
      //   { error: "..." }              — custom views
      //   { detail: "..." }             — DRF default
      //   { non_field_errors: ["..."] } — serializer validation
      //   { email: ["..."] }            — field-level errors
      const backendError =
        data?.error ||
        data?.detail ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        (Array.isArray(data?.email) && data.email[0]);

      const errorMessage = ERROR_MESSAGES[backendError] || backendError || "An unexpected error occurred.";

      // Diagnostic logging (survives prod terser strip)
      console.error('[Login] Failed:', { status, backendError, errorMessage });

      setError(errorMessage);
    } finally {
      submitDebounceRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async (response) => {
    try {
      const id_token = response.credential;
      await loginWithGoogle(
        id_token,
        'login',
        () => {
          setTimeout(() => {
            // Compute target: prioritize location state, then query param, then default
            const from = location.state?.from?.pathname;
            const urlParams = new URLSearchParams(window.location.search);
            const redirect = urlParams.get('redirect');

            const target = from && from !== '/login' && from !== '/signup'
              ? from
              : safeInternalPath(redirect) || AFTER_LOGIN_PATH;

            // R-TRAINING (T9): the junior case only (roles from storage —
            // this callback's context value predates the login).
            landingFor(target, null).then((next) => navigate(next, { replace: true }));
          }, 100);
        },
        getGoogleOAuthRedirectUri()
      );
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      const backendError =
        data?.error ||
        data?.detail ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]);
      const errorMessage = backendError || 'An unexpected error occurred during Google login.';
      console.error('[Login] Google failed:', { status, backendError, message: err.message });
      setError(errorMessage);
    }
  };

  // Don't render form until auth is hydrated
  if (!hydrated || loading) {
    return (
      <div className="login-form" style={{ textAlign: 'center', padding: 'var(--ff-space-3xl) 0' }}>
        <FroggySpinner size="lg" label="Loading" />
      </div>
    );
  }

  // If authenticated, don't render form (redirect effect will handle navigation)
  if (isAuthenticated) {
    return (
      <div className="login-form" style={{ textAlign: 'center', padding: 'var(--ff-space-3xl) 0' }}>
        <FroggySpinner size="lg" label="Redirecting" />
      </div>
    );
  }

  return (
    <div className="login-form">
      <h2>Login</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="formEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => {
              lastInputTimestampRef.current = Date.now();
              setEmail(e.target.value);
            }}
            onFocus={() => {
              lastInputTimestampRef.current = Date.now();
            }}
          />
        </Form.Group>
        <Form.Group controlId="formPassword">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              lastInputTimestampRef.current = Date.now();
              setPassword(e.target.value);
            }}
            onFocus={() => {
              lastInputTimestampRef.current = Date.now();
            }}
          />
        </Form.Group>
        <div className="d-flex justify-content-end mb-3">
          <span className="signup-link" onClick={() => navigate('/reset-password')} role="button" style={{ fontSize: '0.9rem' }}>
            Forgot password?
          </span>
        </div>
        <button type="submit" className="ff-btn ff-btn--primary ff-btn--block" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in...' : 'Login'}
        </button>
      </Form>

      <div className="signup-redirect mt-3 text-center">
        <span>
          Don’t have an account?{' '}
          <span className="signup-link" onClick={() => navigate('/signup')} role="button">
            Sign up
          </span>
        </span>
      </div>

      <div className="social-login mt-4">
      <GoogleLogin
      onSuccess={handleGoogleLogin}
      onError={() => setError('Google signup failed.')}
      useOneTap={false}
      ux_mode="popup"
      type="standard"
      scope="openid profile email"
    />
      </div>
    </div>
  );
};

export default Login;